import React, { useState, useEffect } from 'react';
import './NewArrivals.css';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';

const NewArrivals = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const [arrivals, setArrivals] = useState([]);

  useEffect(() => {
    fetch('/api/new-arrivals')
      .then((res) => res.json())
      .then((data) => {
        setArrivals(data);
      })
      .catch((err) => {
        console.error('Error fetching new arrivals:', err);
      });
  }, []);

  // Split arrivals into their layout slots
  const largeCardFromApi = arrivals.find((a) => a.layoutStyle === 'large-card');
  const horizontalCardFromApi = arrivals.find((a) => a.layoutStyle === 'horizontal-card');

  const largeCard = {
    ...(largeCardFromApi || {}),
    title: 'Premium Smartphone Accessories',
    description: 'Chargers, cases, cables and power banks for every smartphone.',
    image: '/images/newarrival_phone_accessories.png',
    link: largeCardFromApi?.link || '#explore-products',
  };

  const horizontalCard = {
    ...(horizontalCardFromApi || {}),
    title: 'Premium Laptop Accessories',
    description: 'Cooling pads, bags, hubs and essentials for every laptop.',
    image: '/images/newarrival_laptop_accessories.png',
    link: horizontalCardFromApi?.link || '#explore-products',
  };

  // Find all square cards (max 2 for standard layout, but can show more or fallback)
  const squareCards = arrivals.filter((a) => a.layoutStyle === 'square-card');
  const defaultSquareCards = [
    {
      title: 'Wireless Audio',
      description: 'High-fidelity noise cancelling audio.',
      image: '/images/headphone.png',
      link: '#explore-products',
    },
    {
      title: 'Smart Wearables',
      description: 'Stay active, track health and alerts.',
      image: '/images/watch.png',
      link: '#explore-products',
    },
  ];

  // Fill in with defaults if there are fewer than 2 square cards in database
  const activeSquareCards = [...squareCards];
  if (activeSquareCards.length < 2) {
    activeSquareCards.push(...defaultSquareCards.slice(activeSquareCards.length));
  }

  const translateTitle = (title) => {
    if (!title) return '';
    const lower = title.toLowerCase().trim();
    if (lower === 'premium smartphone accessories' || lower === 'flagship smart phones') return t.newArrivalLargeTitle || title;
    if (lower === 'premium laptop accessories' || lower === 'premium laptops') return t.newArrivalHorizontalTitle || title;
    if (lower === 'wireless audio') return t.newArrivalSquare1Title || title;
    if (lower === 'smart wearables') return t.newArrivalSquare2Title || title;
    return title;
  };

  const translateDesc = (desc) => {
    if (!desc) return '';
    const lower = desc.toLowerCase().trim();
    if (
      lower === 'chargers, cases, cables and power banks for every smartphone.' ||
      lower === 'experience the ultimate performance and camera quality with our latest models.'
    ) return t.newArrivalLargeDesc || desc;
    if (
      lower === 'cooling pads, bags, hubs and essentials for every laptop.' ||
      lower === 'sleek, powerful ultrabooks designed for productivity and creators.'
    ) return t.newArrivalHorizontalDesc || desc;
    if (lower === 'high-fidelity noise cancelling audio.') return t.newArrivalSquare1Desc || desc;
    if (lower === 'stay active, track health and alerts.') return t.newArrivalSquare2Desc || desc;
    return desc;
  };

  const getLink = (link) => (link === '#shop' || !link) ? '#explore-products' : link;

  const handleShopNowClick = (e, link) => {
    if (link && link.startsWith('#')) {
      e.preventDefault();
      const sectionId = link.substring(1);

      if (window.location.pathname !== '/' && window.location.pathname !== '') {
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }

      const tryScroll = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };

      if (!tryScroll()) {
        let attempts = 0;
        const timer = setInterval(() => {
          attempts++;
          if (tryScroll() || attempts >= 20) {
            clearInterval(timer);
          }
        }, 50);
      }
    }
  };

  return (
    <section id="new-arrivals" className="new-arrivals-section-wrapper">
      <div className="new-arrivals-section-container">
        {/* Section Title */}
        <div className="section-header-block">
          <div className="section-label-container">
            <div className="label-badge"></div>
            <span className="label-text">{t.featureToday}</span>
          </div>
          <div className="section-title-row">
            <h2 className="section-title">{t.newArrival}</h2>
          </div>
        </div>

        {/* New Arrivals Grid */}
        <div className="new-arrivals-grid">
          {/* Left Large Card - Smartphone */}
          <div className="featured-card large-card">
            <img src={largeCard.image} alt={largeCard.title} className="featured-card-img" />
            <div className="featured-card-content">
              <h3 className="featured-title">{translateTitle(largeCard.title)}</h3>
              <p className="featured-desc">{translateDesc(largeCard.description)}</p>
              <a href={getLink(largeCard.link)} className="featured-shop-link" onClick={(e) => handleShopNowClick(e, getLink(largeCard.link))}>{t.shopNow}</a>
            </div>
          </div>

          {/* Right Layout */}
          <div className="featured-right-group">
            {/* Top Wide Card - Laptops */}
            <div className="featured-card horizontal-card">
              <div className="featured-card-content">
                <h3 className="featured-title">{translateTitle(horizontalCard.title)}</h3>
                <p className="featured-desc">{translateDesc(horizontalCard.description)}</p>
                <a href={getLink(horizontalCard.link)} className="featured-shop-link" onClick={(e) => handleShopNowClick(e, getLink(horizontalCard.link))}>{t.shopNow}</a>
              </div>
              <img src={horizontalCard.image} alt={horizontalCard.title} className="featured-card-img" />
            </div>

            {/* Bottom Two Square Cards */}
            <div className="featured-bottom-row">
              {activeSquareCards.slice(0, 2).map((card, idx) => (
                <div key={card._id || idx} className="featured-card square-card">
                  <img src={card.image} alt={card.title} className="featured-card-img" />
                  <div className="featured-card-content">
                    <h3 className="featured-title">{translateTitle(card.title)}</h3>
                    <p className="featured-desc">{translateDesc(card.description)}</p>
                    <a href={getLink(card.link)} className="featured-shop-link" onClick={(e) => handleShopNowClick(e, getLink(card.link))}>{t.shopNow}</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewArrivals;
